import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check } from 'lucide-react';
import { Language } from '../App';
interface PublishingCycleProps {
  language: Language;
}
export function PublishingCycle({
  language
}: PublishingCycleProps) {
  const isPashto = language === 'pashto';
  const currentMonth = new Date().getMonth(); // 0-11
  // Define the 6 bi-monthly issues
  const issues = [{
    periodEn: 'January – February',
    periodPs: 'جنوري – فبروري',
    labelEn: 'Jan–Feb Issue',
    labelPs: 'جنوري–فبروري ګڼه',
    months: [0, 1]
  }, {
    periodEn: 'March – April',
    periodPs: 'مارچ – اپریل',
    labelEn: 'Mar–Apr Issue',
    labelPs: 'مارچ–اپریل ګڼه',
    months: [2, 3]
  }, {
    periodEn: 'May – June',
    periodPs: 'مۍ – جون',
    labelEn: 'May–Jun Issue',
    labelPs: 'مۍ–جون ګڼه',
    months: [4, 5]
  }, {
    periodEn: 'July – August',
    periodPs: 'جولای – اګست',
    labelEn: 'Jul–Aug Issue',
    labelPs: 'جولای–اګست ګڼه',
    months: [6, 7]
  }, {
    periodEn: 'September – October',
    periodPs: 'سپتمبر – اکتوبر',
    labelEn: 'Sep–Oct Issue',
    labelPs: 'سپتمبر–اکتوبر ګڼه',
    months: [8, 9]
  }, {
    periodEn: 'November – December',
    periodPs: 'نومبر – دسمبر',
    labelEn: 'Nov–Dec Issue',
    labelPs: 'نومبر–دسمبر ګڼه',
    months: [10, 11]
  }];
  // Determine the index of the current issue
  let activeIndex = 0;
  for (let i = 0; i < issues.length; i++) {
    if (issues[i].months.includes(currentMonth)) {
      activeIndex = i;
      break;
    }
  }
  return <section className="py-10 md:py-14 bg-white px-6 overflow-hidden" data-id="element-236">
      <div className="max-w-5xl mx-auto" data-id="element-237">
        <AnimatePresence mode="wait" data-id="element-238">
          <motion.div key={language} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4
        }} data-id="element-239">
            {/* Header */}
            <div className="text-center mb-8 md:mb-10" data-id="element-240">
              <h2 className={`text-3xl md:text-4xl font-bold text-pasoon-dark mb-3 ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-241">
                {isPashto ? 'هره دوه میاشتې خپریږي' : 'Published Every Two Months'}
              </h2>
              <p className={`text-gray-500 text-base md:text-lg ${isPashto ? 'font-pashto' : ''}`} data-id="element-242">
                {isPashto ? 'د ۲۰۰۵ راهیسې دوامداره خپرونه' : 'Consistent storytelling since 2005'}
              </p>
            </div>

            {/* Timeline */}
            <div data-id="element-243">
              {/* Scrollable container */}
              <div className="-mx-6 px-6 overflow-x-auto scrollbar-hide pb-8" data-id="element-244">
                <div className="relative flex items-start justify-between min-w-[800px] md:min-w-0 pt-8 px-4" data-id="element-245">
                  {/* Background track */}
                  <div className="absolute top-12 left-4 right-4 h-1.5 bg-gray-100 rounded-full -translate-y-1/2" data-id="element-246"></div>

                  {/* Progress fill up to active month */}
                  <motion.div className={`absolute top-12 h-1.5 bg-gradient-to-r from-pasoon-muted to-pasoon-red rounded-full -translate-y-1/2 ${isPashto ? 'right-4' : 'left-4'}`} initial={{
                  width: 0
                }} whileInView={{
                  width: `${activeIndex / (issues.length - 1) * 100}%`
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 1.2,
                  delay: 0.4,
                  ease: [0.16, 1, 0.3, 1]
                }} data-id="element-247" />

                  {/* Nodes */}
                  {issues.map((issue, idx) => {
                  const isCurrent = idx === activeIndex;
                  const isPublished = idx < activeIndex;
                  const isComingSoon = idx > activeIndex;
                  return <motion.div key={idx} className="relative z-10 flex flex-col items-center w-32" initial={{
                    opacity: 0,
                    y: 10
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 0.5,
                    delay: 0.3 + idx * 0.1
                  }} data-id="element-248">
                        {/* Pulse ring for active */}
                        {isCurrent && <motion.div className="absolute top-4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-pasoon-red/20" animate={{
                      scale: [1, 1.5, 1],
                      opacity: [0.5, 0, 0.5]
                    }} transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut'
                    }} data-id="element-249" />}

                        {/* Circle marker */}
                        <div className={`relative w-8 h-8 rounded-full flex items-center justify-center transition-all z-10 ${isCurrent ? 'bg-pasoon-red text-white shadow-lg shadow-red-900/30 scale-125' : isPublished ? 'bg-pasoon-muted text-white' : 'bg-white border-2 border-gray-200 text-gray-300'}`} data-id="element-250">
                          {isPublished && <Check className="w-4 h-4" data-id="element-251" />}
                          {isCurrent && <div className="w-2.5 h-2.5 bg-white rounded-full" data-id="element-252"></div>}
                        </div>

                        {/* Content below marker */}
                        <div className="mt-6 text-center flex flex-col items-center gap-1.5" data-id="element-253">
                          {/* Period Label */}
                          <span className={`text-sm font-bold ${isCurrent ? 'text-pasoon-red' : isPublished ? 'text-pasoon-dark' : 'text-gray-400'} ${isPashto ? 'font-pashto' : ''}`} data-id="element-254">
                            {isPashto ? issue.periodPs : issue.periodEn}
                          </span>

                          {/* Status Badge */}
                          <span className={`text-[10px] md:text-xs font-bold px-2 py-0.5 rounded-full ${isCurrent ? 'bg-red-50 text-pasoon-red' : isPublished ? 'bg-green-50 text-pasoon-muted' : 'bg-gray-100 text-gray-400'} ${isPashto ? 'font-pashto' : 'uppercase tracking-wider'}`} data-id="element-255">
                            {isCurrent ? isPashto ? 'اوسنۍ ګڼه' : 'Current Issue' : isPublished ? isPashto ? 'خپره شوې' : 'Published' : isPashto ? 'ډیر ژر' : 'Coming Soon'}
                          </span>

                          {/* Sub-label (Issue Name) */}
                          <span className={`text-xs mt-1 ${isCurrent ? 'text-gray-600 font-medium' : 'text-gray-400'} ${isPashto ? 'font-pashto' : ''}`} data-id="element-256">
                            {isPashto ? issue.labelPs : issue.labelEn}
                          </span>
                        </div>
                      </motion.div>;
                })}
                </div>
              </div>

              {/* Scroll hint on mobile */}
              <p className={`text-center text-gray-400 text-xs mt-2 md:hidden ${isPashto ? 'font-pashto' : ''}`} data-id="element-257">
                {isPashto ? '← د لیدلو لپاره سوایپ کړئ' : 'Swipe to explore →'}
              </p>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
}